import { Button, MenuItem, MenuSeparator, toast } from '@affine/component';
import { usePageHelper } from '@affine/core/blocksuite/block-suite-page-list/utils';
import {
  STREAMLAIN_DROPDOWN_MENU_OFFSET,
  STREAMLAIN_DROPDOWN_MENU_SIDE_START_ALIGN_OFFSET,
} from '@affine/core/components/dropdown-panel-chrome/chrome';
import { StreamLainMenu } from '@affine/core/components/dropdown-panel-chrome/radix-menu';
import { StreamLainMenuSub } from '@affine/core/components/dropdown-panel-chrome/radix-menu-sub';
import { useAsyncCallback } from '@affine/core/components/hooks/affine-async-hooks';
import {
  DocsService,
  isCreateDocPermissionError,
} from '@affine/core/modules/doc';
import type { DocCreateConfig } from '@affine/core/modules/doc-share';
import { EditorSettingService } from '@affine/core/modules/editor-setting';
import { ObjectPermissionMutationService } from '@affine/core/modules/permissions';
import { TemplateDocService } from '@affine/core/modules/template-doc';
import { TemplateListMenuContentScrollable } from '@affine/core/modules/template-doc/view/template-list-menu';
import { inferOpenMode } from '@affine/core/utils';
import { useI18n } from '@affine/i18n';
import track from '@affine/track';
import type { DocMode } from '@blocksuite/affine/model';
import {
  ArrowDownSmallIcon,
  EdgelessIcon,
  PageIcon,
  PlusIcon,
  TemplateIcon,
} from '@blocksuite/icons/rc';
import { useLiveData, useService } from '@toeverything/infra';
import clsx from 'clsx';
import type React from 'react';
import { type MouseEvent, useCallback, useRef } from 'react';

import { SidebarCreateButton } from '../sidebar-create-button';
import * as styles from './index.css';

/**
 * @return a function to create a new doc, will duplicate the template doc if the page template is enabled
 */
const useNewDoc = () => {
  const t = useI18n();
  const objectPermissionMutation = useService(ObjectPermissionMutationService);
  const templateDocService = useService(TemplateDocService);
  const docsService = useService(DocsService);

  const enablePageTemplate = useLiveData(
    templateDocService.setting.enablePageTemplate$
  );
  const pageTemplateDocId = useLiveData(
    templateDocService.setting.pageTemplateDocId$
  );

  const pageHelper = usePageHelper();

  const applyDocPermissions = useCallback(
    (docId: string, config: DocCreateConfig) => {
      void objectPermissionMutation
        .grantPendingAssignments({ id: docId, kind: 'doc' }, config)
        .catch(error => {
          console.error('Failed to apply document permissions:', error);
          toast(
            t[
              'com.streamlain.rootAppSidebar.organize.folder.error.doc-permissions-failed'
            ]()
          );
        });
    },
    [objectPermissionMutation, t]
  );

  const createPage = useAsyncCallback(
    async (
      e?: MouseEvent,
      mode?: DocMode,
      targetFolderId?: string | null,
      config?: DocCreateConfig,
      atOverride?: 'new-tab' | 'tail' | 'active',
      onCreated?: (docId: string) => void,
      createInWorkspaceRoot = false
    ) => {
      const at = atOverride ?? inferOpenMode(e);
      if (
        !config &&
        !targetFolderId &&
        createInWorkspaceRoot &&
        enablePageTemplate &&
        pageTemplateDocId
      ) {
        try {
          const docId = await docsService.duplicateFromTemplate(
            pageTemplateDocId,
            undefined,
            { createInWorkspaceRoot: true, userInitiated: true }
          );
          onCreated?.(docId);
        } catch (error) {
          if (isCreateDocPermissionError(error)) {
            toast(
              t['com.streamlain.rootAppSidebar.newDoc.error.no-permission']()
            );
          }
          throw error;
        }
      } else {
        const page = pageHelper.createPage(mode, {
          at,
          show: false,
          targetFolderId: targetFolderId ?? undefined,
          createInWorkspaceRoot,
          title: config?.name,
        });
        onCreated?.(page.id);
        if (config) {
          applyDocPermissions(page.id, config);
        }
      }
    },
    [
      applyDocPermissions,
      docsService,
      enablePageTemplate,
      pageHelper,
      pageTemplateDocId,
      t,
    ]
  );

  return createPage;
};

interface AddPageButtonProps {
  className?: string;
  createItems?: React.ReactNode;
  dynamicTargets?: AddPageButtonTarget[];
  onDocCreated?: (docId: string) => void;
  onFolderDocCreated?: (folderId: string, docId: string) => void;
  showPublicItems?: boolean;
  style?: React.CSSProperties;
  tooltip?: string;
  privateFolderId?: string | null;
}

const createDocCreatedHandler =
  (
    targetFolderId: string | null | undefined,
    onDocCreated?: (docId: string) => void,
    onFolderDocCreated?: (folderId: string, docId: string) => void
  ) =>
  (docId: string) => {
    onDocCreated?.(docId);
    if (targetFolderId) {
      onFolderDocCreated?.(targetFolderId, docId);
    }
  };

export interface AddPageButtonTarget {
  folderId: string;
  label: string;
}

const createButtonTooltipOptions = { side: 'top' as const };
const createMenuContentOptions = {
  align: 'start' as const,
  alignOffset: -STREAMLAIN_DROPDOWN_MENU_SIDE_START_ALIGN_OFFSET,
  className: styles.createMenuContent,
  collisionPadding: 8,
  side: 'right' as const,
  sideOffset: STREAMLAIN_DROPDOWN_MENU_OFFSET,
};
const withCreateMenuSections = ({
  createItems,
  dynamicItems,
  primaryItems,
}: {
  createItems?: React.ReactNode;
  dynamicItems?: React.ReactNode;
  primaryItems?: React.ReactNode;
}) => {
  const hasPrimaryItems = !!primaryItems;
  const hasDynamicItems = !!dynamicItems;

  return (
    <>
      {primaryItems}
      {dynamicItems ? (
        <>
          {hasPrimaryItems ? <MenuSeparator /> : null}
          {dynamicItems}
        </>
      ) : null}
      {createItems ? (
        <>
          {hasPrimaryItems || hasDynamicItems ? <MenuSeparator /> : null}
          {createItems}
        </>
      ) : null}
    </>
  );
};

const renderCreateMenuContent = (items: React.ReactNode) => (
  <div className={styles.createMenuScroll}>{items}</div>
);

const getMenuMode = (
  privateFolderId?: string | null,
  dynamicTargets?: AddPageButtonTarget[],
  createItems?: React.ReactNode,
  showPublicItems = true
) =>
  !showPublicItems ||
  privateFolderId ||
  (dynamicTargets?.length ?? 0) > 0 ||
  createItems
    ? 'menu'
    : 'button';

const renderDefaultDynamicTargetItems = (
  dynamicTargets: AddPageButtonTarget[] | undefined,
  openCreateDialog: (
    e?: MouseEvent,
    targetFolderId?: string | null,
    createInWorkspaceRoot?: boolean
  ) => void
) =>
  dynamicTargets && dynamicTargets.length > 0 ? (
    <>
      {dynamicTargets.map(target => (
        <MenuItem
          key={target.folderId}
          prefixIcon={<PageIcon />}
          onClick={e => openCreateDialog(e, target.folderId)}
          onAuxClick={e => openCreateDialog(e, target.folderId)}
        >
          {target.label}
        </MenuItem>
      ))}
    </>
  ) : null;

export function AddPageButton(props: AddPageButtonProps) {
  const editorSetting = useService(EditorSettingService);
  const newDocDefaultMode = useLiveData(
    editorSetting.editorSetting.settings$.selector(s => s.newDocDefaultMode)
  );

  return newDocDefaultMode === 'ask' ? (
    <AddPageWithAsk {...props} />
  ) : (
    <AddPageWithoutAsk {...props} />
  );
}

function AddPageWithAsk({
  className,
  createItems,
  dynamicTargets,
  onDocCreated,
  onFolderDocCreated,
  showPublicItems = true,
  style,
  tooltip,
  privateFolderId,
}: AddPageButtonProps) {
  const t = useI18n();
  const triggerRef = useRef<HTMLButtonElement | null>(null);
  const createDoc = useNewDoc();
  const docsService = useService(DocsService);
  const openCreateDialog = useCallback(
    (
      e?: MouseEvent,
      targetFolderId?: string | null,
      createInWorkspaceRoot = false
    ) => {
      void createDoc(
        e,
        'page',
        targetFolderId,
        undefined,
        undefined,
        createDocCreatedHandler(
          targetFolderId,
          onDocCreated,
          onFolderDocCreated
        ),
        createInWorkspaceRoot
      );
      track.$.navigationPanel.$.createDoc();
      track.$.sidebar.newDoc.quickStart({ with: 'page' });
    },
    [createDoc, onDocCreated, onFolderDocCreated]
  );
  const openCreateEdgelessDialog = useCallback(
    (
      e?: MouseEvent,
      targetFolderId?: string | null,
      createInWorkspaceRoot = false
    ) => {
      void createDoc(
        e,
        'edgeless',
        targetFolderId,
        undefined,
        undefined,
        createDocCreatedHandler(
          targetFolderId,
          onDocCreated,
          onFolderDocCreated
        ),
        createInWorkspaceRoot
      );
      track.$.navigationPanel.$.createDoc();
      track.$.sidebar.newDoc.quickStart({ with: 'edgeless' });
    },
    [createDoc, onDocCreated, onFolderDocCreated]
  );

  const createDocFromTemplate = useAsyncCallback(
    async (templateId: string) => {
      const docId = await docsService.duplicateFromTemplate(
        templateId,
        undefined,
        { createInWorkspaceRoot: true, userInitiated: true }
      );
      onDocCreated?.(docId);
      track.$.sidebar.newDoc.quickStart({ with: 'template' });
    },
    [docsService, onDocCreated]
  );

  const publicItems = showPublicItems ? (
    <>
      <MenuItem
        prefixIcon={<PageIcon />}
        onClick={e => openCreateDialog(e, null, true)}
        onAuxClick={e => openCreateDialog(e, null, true)}
      >
        {t['Page']()}
      </MenuItem>
      <MenuItem
        prefixIcon={<EdgelessIcon />}
        onClick={e => openCreateEdgelessDialog(e, null, true)}
        onAuxClick={e => openCreateEdgelessDialog(e, null, true)}
      >
        {t['Edgeless']()}
      </MenuItem>
      <StreamLainMenuSub
        triggerOptions={{
          prefixIcon: <TemplateIcon />,
        }}
        subContentOptions={{
          sideOffset: 16,
          className: clsx(styles.createSubMenuContent, styles.templateMenu),
        }}
        items={
          <TemplateListMenuContentScrollable onSelect={createDocFromTemplate} />
        }
      >
        {t['Template']()}
      </StreamLainMenuSub>
    </>
  ) : null;

  const privateItems = privateFolderId ? (
    <>
      <MenuItem
        prefixIcon={<PageIcon />}
        onClick={e => openCreateDialog(e, privateFolderId)}
        onAuxClick={e => openCreateDialog(e, privateFolderId)}
      >
        {t['Page']()}
      </MenuItem>
      <MenuItem
        prefixIcon={<EdgelessIcon />}
        onClick={e => openCreateEdgelessDialog(e, privateFolderId)}
        onAuxClick={e => openCreateEdgelessDialog(e, privateFolderId)}
      >
        {t['Edgeless']()}
      </MenuItem>
    </>
  ) : null;
  const dynamicItems =
    dynamicTargets && dynamicTargets.length > 0 ? (
      <>
        {dynamicTargets.map(target => (
          <StreamLainMenuSub
            key={target.folderId}
            triggerOptions={{
              prefixIcon: <PageIcon />,
            }}
            subContentOptions={{
              sideOffset: 16,
              className: styles.createSubMenuContent,
            }}
            items={
              <>
                <MenuItem
                  prefixIcon={<PageIcon />}
                  onClick={e => openCreateDialog(e, target.folderId)}
                  onAuxClick={e => openCreateDialog(e, target.folderId)}
                >
                  {t['Page']()}
                </MenuItem>
                <MenuItem
                  prefixIcon={<EdgelessIcon />}
                  onClick={e => openCreateEdgelessDialog(e, target.folderId)}
                  onAuxClick={e => openCreateEdgelessDialog(e, target.folderId)}
                >
                  {t['Edgeless']()}
                </MenuItem>
              </>
            }
          >
            {target.label}
          </StreamLainMenuSub>
        ))}
      </>
    ) : null;
  const primaryItems =
    publicItems && privateItems ? (
      <>
        <StreamLainMenuSub
          triggerOptions={{
            prefixIcon: <PageIcon />,
          }}
          subContentOptions={{
            sideOffset: 16,
            className: styles.createSubMenuContent,
          }}
          items={publicItems}
        >
          {t['com.streamlain.rootAppSidebar.newDoc.public']()}
        </StreamLainMenuSub>
        <StreamLainMenuSub
          triggerOptions={{
            prefixIcon: <PageIcon />,
          }}
          subContentOptions={{
            sideOffset: 16,
            className: styles.createSubMenuContent,
          }}
          items={privateItems}
        >
          {t['com.streamlain.rootAppSidebar.newDoc.private']()}
        </StreamLainMenuSub>
      </>
    ) : (
      (publicItems ?? privateItems)
    );

  if (!primaryItems && !dynamicItems && !createItems) {
    return null;
  }

  const menuItems = withCreateMenuSections({
    primaryItems,
    dynamicItems,
    createItems,
  });

  return (
    <>
      <StreamLainMenu
        triggerRef={triggerRef}
        contentOptions={createMenuContentOptions}
        items={renderCreateMenuContent(menuItems)}
      >
        <Button
          ref={triggerRef}
          tooltip={tooltip ?? t['New Page']()}
          tooltipOptions={createButtonTooltipOptions}
          data-testid="sidebar-new-page-with-ask-button"
          className={clsx([styles.withAskRoot, className])}
          style={style}
        >
          <div className={styles.withAskContent}>
            <PlusIcon />
            <ArrowDownSmallIcon />
          </div>
        </Button>
      </StreamLainMenu>
    </>
  );
}

function AddPageWithoutAsk({
  className,
  createItems,
  dynamicTargets,
  onDocCreated,
  onFolderDocCreated,
  showPublicItems = true,
  style,
  tooltip,
  privateFolderId,
}: AddPageButtonProps) {
  const triggerRef = useRef<HTMLButtonElement | null>(null);
  const createDoc = useNewDoc();
  const openCreateDialog = useCallback(
    (
      e?: MouseEvent,
      targetFolderId?: string | null,
      createInWorkspaceRoot = false
    ) => {
      void createDoc(
        e,
        'edgeless',
        targetFolderId,
        undefined,
        undefined,
        createDocCreatedHandler(
          targetFolderId,
          onDocCreated,
          onFolderDocCreated
        ),
        createInWorkspaceRoot
      );
      track.$.navigationPanel.$.createDoc();
      track.$.sidebar.newDoc.quickStart({ with: 'edgeless' });
    },
    [createDoc, onDocCreated, onFolderDocCreated]
  );

  const t = useI18n();
  const menuMode = getMenuMode(
    privateFolderId,
    dynamicTargets,
    createItems,
    showPublicItems
  );
  const dynamicItems = renderDefaultDynamicTargetItems(
    dynamicTargets,
    openCreateDialog
  );

  if (
    menuMode === 'menu' &&
    !showPublicItems &&
    !privateFolderId &&
    !dynamicItems &&
    !createItems
  ) {
    return null;
  }

  if (menuMode === 'menu') {
    const primaryItems =
      showPublicItems || privateFolderId ? (
        <>
          {showPublicItems ? (
            <MenuItem
              prefixIcon={<PageIcon />}
              onClick={e => openCreateDialog(e, null, true)}
              onAuxClick={e => openCreateDialog(e, null, true)}
            >
              {t['com.streamlain.rootAppSidebar.newDoc.public']()}
            </MenuItem>
          ) : null}
          {privateFolderId ? (
            <MenuItem
              prefixIcon={<PageIcon />}
              onClick={e => openCreateDialog(e, privateFolderId)}
              onAuxClick={e => openCreateDialog(e, privateFolderId)}
            >
              {t['com.streamlain.rootAppSidebar.newDoc.private']()}
            </MenuItem>
          ) : null}
        </>
      ) : null;

    return (
      <>
        <StreamLainMenu
          triggerRef={triggerRef}
          contentOptions={createMenuContentOptions}
          items={renderCreateMenuContent(
            withCreateMenuSections({
              primaryItems,
              dynamicItems,
              createItems,
            })
          )}
        >
          <SidebarCreateButton
            ref={triggerRef}
            label={tooltip ?? t['New Page']()}
            testId="sidebar-new-page-button"
            style={style}
            className={className}
            tooltipOptions={createButtonTooltipOptions}
          />
        </StreamLainMenu>
      </>
    );
  }

  return (
    <>
      <SidebarCreateButton
        label={tooltip ?? t['New Page']()}
        testId="sidebar-new-page-button"
        style={style}
        className={className}
        onClick={e => openCreateDialog(e, null, true)}
        onAuxClick={e => openCreateDialog(e, null, true)}
        tooltipOptions={createButtonTooltipOptions}
      />
    </>
  );
}
